import { useEffect, useState } from 'react'
import type { AccessRulesPreview, TenantAccessRules } from '@/lib/apiTypes'
import { useAccessRules } from '@/lib/hooks'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { PageLoading, EmptyState } from '@/shared/components/EmptyState'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { Switch } from '@/shared/components/Switch'
import { Tooltip } from '@/shared/components/Tooltip'
import { useToast } from '@/context/ToastContext'

/**
 * Org network access rules: where this org's machine callers (bot tokens and CI OIDC) - and, when
 * opted in, humans - may act from. One rule set per org, in ALLOW or DENY mode over countries, ASNs
 * and CIDRs, with an Enabled switch so a list can be drafted and previewed before it is armed.
 * The Worker decides from the Cloudflare edge's view of each request; see lib/tenantAccessRules.ts.
 */

type Draft = Omit<TenantAccessRules, 'updated_at' | 'asns'> & { asnsText: string; countriesText: string; cidrsText: string }

const splitList = (text: string): string[] => [...new Set(text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean))]

function toDraft(r: TenantAccessRules): Draft {
  return {
    enabled: r.enabled,
    mode: r.mode,
    applies_to: r.applies_to,
    countries: r.countries,
    cidrs: r.cidrs,
    countriesText: r.countries.join(', '),
    asnsText: r.asns.join(', '),
    cidrsText: r.cidrs.join(', '),
  }
}

function toBody(d: Draft) {
  return {
    enabled: d.enabled,
    mode: d.mode,
    applies_to: d.applies_to,
    countries: splitList(d.countriesText).map((c) => c.toUpperCase()),
    asns: splitList(d.asnsText),
    cidrs: splitList(d.cidrsText),
  }
}

const labelStyle = { display: 'block', fontSize: '0.75rem', color: 'var(--text2)', marginBottom: 4 } as const
const hintStyle = { fontSize: '0.72rem', color: 'var(--text3)', marginTop: 4, marginBottom: 0 } as const

export function AccessRulesTab({ orgId, canAdmin }: { orgId: string; canAdmin: boolean }) {
  const rules = useAccessRules(orgId)
  const { showToast } = useToast()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<AccessRulesPreview | null>(null)
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    if (rules.data && !draft) setDraft(toDraft(rules.data))
  }, [rules.data, draft])

  const listCount = draft ? splitList(draft.countriesText).length + splitList(draft.asnsText).length + splitList(draft.cidrsText).length : 0
  const dirty = !!draft && !!rules.data && JSON.stringify(toBody(draft)) !== JSON.stringify(toBody(toDraft(rules.data)))

  async function save(next: Draft) {
    setBusy(true)
    setError(null)
    try {
      const saved = await api.put<TenantAccessRules>(`/api/orgs/${orgId}/access-rules`, toBody(next))
      setDraft(toDraft(saved))
      setPreview(null)
      await rules.refetch()
      showToast(saved.enabled ? 'Access rules saved and enabled.' : 'Access rules saved (disabled).', 'success')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function runPreview() {
    if (!draft) return
    setPreviewing(true)
    setError(null)
    try {
      setPreview(await api.post<AccessRulesPreview>(`/api/orgs/${orgId}/access-rules/preview`, toBody(draft)))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setPreviewing(false)
    }
  }

  async function remove() {
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/api/orgs/${orgId}/access-rules`)
      const fresh = await rules.refetch()
      setDraft(fresh ? toDraft(fresh) : null)
      setPreview(null)
      showToast('Access rules removed.', 'success')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  if (rules.loading || (!draft && !rules.error)) return <PageLoading />
  if (rules.error) {
    const e = rules.error.toLowerCase()
    return e.includes('forbidden') || e.includes('not authorized') || e.includes('permission')
      ? <EmptyState icon="🔒" title="Access restricted" subtitle="Access rules are only available to org admins and owners." />
      : <FeedbackMessage ok={false} msg={rules.error} />
  }
  if (!draft) return null

  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch })

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      <p style={{ fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text2)', margin: 0 }}>
        Restrict where this organisation's machine callers - bot tokens and CI pipelines - may connect from. Decided at
        the edge on every request from the caller's country, network (ASN) and IP; a refused request gets
        a <code>403 access_rule_denied</code> that never reveals the rule. Humans are exempt unless you opt them in.
      </p>

      <div className="card" style={{ padding: 16, display: 'grid', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
              Enforcement{' '}
              <span className={`badge ${draft.enabled ? 'badge-green' : 'badge-neutral'}`} style={{ marginLeft: 6 }}>{draft.enabled ? 'on' : 'off'}</span>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>
              {rules.data?.updated_at ? `Last saved ${formatDate(rules.data.updated_at)}.` : 'Never saved.'} Draft the lists, preview, then switch on.
            </div>
          </div>
          <Switch
            label="Enforce access rules"
            checked={draft.enabled}
            disabled={!canAdmin || listCount === 0}
            busy={busy}
            onChange={(next) => set({ enabled: next })}
          />
        </div>

        <div>
          <label style={labelStyle}>Mode</label>
          <div role="group" aria-label="Rule mode" className="seg">
            {(['allow', 'deny'] as const).map((m) => (
              <button key={m} type="button" className="seg-btn" aria-pressed={draft.mode === m} disabled={!canAdmin} onClick={() => set({ mode: m })}>
                {m === 'allow' ? 'Allow only' : 'Deny'}
              </button>
            ))}
          </div>
          <p style={hintStyle}>
            {draft.mode === 'allow'
              ? 'A request must match at least one listed country, ASN or CIDR. Anything else - including an origin the edge could not classify - is refused.'
              : 'A request matching any listed country, ASN or CIDR is refused. An origin the edge could not classify is refused too when the relevant list is non-empty.'}
          </p>
        </div>

        <div>
          <label style={labelStyle}>Countries</label>
          <input className="input" style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: '0.8rem' }} placeholder="GB, IE, DE" value={draft.countriesText} disabled={!canAdmin} onChange={(e) => set({ countriesText: e.target.value })} />
          <p style={hintStyle}>ISO-3166 alpha-2 codes. <code>T1</code> is Tor exit traffic and <code>XX</code> is unclassified.</p>
        </div>
        <div>
          <label style={labelStyle}>
            <Tooltip text="Hosted CI runners sit in a small set of networks while their IP ranges churn, so ASN is usually the right handle for 'only our pipelines'." placement="top">
              <span tabIndex={0} className="cursor-help">Networks (ASN)</span>
            </Tooltip>
          </label>
          <input className="input" style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: '0.8rem' }} placeholder="8075, AS16509" value={draft.asnsText} disabled={!canAdmin} onChange={(e) => set({ asnsText: e.target.value })} />
          <p style={hintStyle}>Autonomous system numbers, with or without the <code>AS</code> prefix.</p>
        </div>
        <div>
          <label style={labelStyle}>IP ranges (CIDR)</label>
          <input className="input" style={{ width: '100%', fontFamily: 'var(--mono)', fontSize: '0.8rem' }} placeholder="203.0.113.0/24, 2001:db8::/32" value={draft.cidrsText} disabled={!canAdmin} onChange={(e) => set({ cidrsText: e.target.value })} />
          <p style={hintStyle}>IPv4 or IPv6. A bare address is a single host.</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 4, borderTop: '1px solid var(--border2)' }}>
          <div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text)' }}>Apply to humans too</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>
              Also refuse portal, API and CLI use by members from outside the rule. The save is refused if it would lock <em>you</em> out.
            </div>
          </div>
          <Switch
            label="Apply access rules to humans"
            checked={draft.applies_to === 'all'}
            disabled={!canAdmin}
            busy={busy}
            onChange={(next) => set({ applies_to: next ? 'all' : 'machine' })}
          />
        </div>

        {preview && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text2)', padding: '10px 12px', background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 6 }}>
            <strong style={{ color: preview.would_deny > 0 ? 'var(--amber)' : 'var(--green)' }}>
              Would have refused {preview.would_deny} of {preview.sampled}
            </strong>{' '}
            recent origins ({preview.sources.machine_clients} tokens' last-seen, {preview.sources.state_locks} current lock holders).
            {preview.denied_by_country.length > 0 && (
              <> Refused by country: {preview.denied_by_country.map((d) => `${d.country} ×${d.count}`).join(', ')}.</>
            )}
            {preview.note && <div style={{ marginTop: 6, color: 'var(--text3)' }}>{preview.note}</div>}
          </div>
        )}

        {error && <div className="feedback-error">{error}</div>}

        {canAdmin && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {rules.data?.updated_at && (
              <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => { void remove() }} style={{ marginRight: 'auto' }}>
                Remove rules
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm" disabled={busy || previewing || listCount === 0} onClick={() => { void runPreview() }}>
              {previewing ? <span className="spinner" /> : 'Preview'}
            </button>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !dirty} onClick={() => { void save(draft) }}>
              {busy ? <span className="spinner" /> : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
